import { CommentCommunication } from '@seen/core/models/communications/CommentCommunication'
import { ORDER_STATUS } from '@seen/core/models/orders/constants'
import type { Order } from '@seen/core/models/orders/Order'
import { sortOrdersByTier } from '@seen/core/models/orders/sorting'
import {
  Button,
  cn,
  IconComment,
  KBD,
  TypographyDetail,
  TypographySubtle,
} from '@seen/ui'
import { IconArrowUp, IconCopy, IconLink } from '@tabler/icons-react'
import { groupBy, uniqBy } from 'lodash-es'
import {
  type JSX,
  memo,
  type ReactNode,
  useCallback,
  useMemo,
  useState,
} from 'react'
import { z } from 'zod'
import { BulkActionBar } from '#src/components/core/BulkActionBar.tsx'
import { Dialog } from '#src/components/core/dialog/Dialog.tsx'
import { DialogButton } from '#src/components/core/dialog/DialogButton.tsx'
import { DialogContent } from '#src/components/core/dialog/DialogContent.tsx'
import { DialogFooter } from '#src/components/core/dialog/DialogFooter.tsx'
import { DialogDisplay } from '#src/components/core/dialog/display/DialogDisplay.tsx'
import { Form } from '#src/components/core/form/Form.tsx'
import { FormField } from '#src/components/core/form/FormField.tsx'
import { SubmitButton } from '#src/components/core/form/SubmitButton.tsx'
import { Loader } from '#src/components/core/Loader.tsx'
import { Tooltip } from '#src/components/core/tooltip/Tooltip.tsx'
import { TooltipContent } from '#src/components/core/tooltip/TooltipContent.tsx'
import { TooltipTrigger } from '#src/components/core/tooltip/TooltipTrigger.tsx'
import { VirtualizedList } from '#src/components/core/VirtualizedList.tsx'
import { IconOrderStatus } from '#src/components/icons/IconOrderStatus.tsx'
import { OrderListRow } from '#src/components/order/OrderListRow.tsx'
import { OrderPerformerGroupHeader } from '#src/components/order/OrderPerformerGroupHeader.tsx'
import { useOrderAppointmentsMap } from '#src/components/order/useOrderAppointmentsMap.ts'
import type { OrderViewDisplay } from '#src/components/order/useOrdersPageStore.ts'
import { RichTextEditor } from '#src/components/rich-text-editor/RichTextEditor.tsx'
import { TaskRowCheckbox } from '#src/components/TaskRowCheckbox.tsx'
import type { MatchInfo } from '#src/hooks/useClientSideSearch.ts'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useTransactionMutation } from '#src/hooks/useFhirBundleMutation.ts'
import { useForm } from '#src/hooks/useForm.ts'
import { useHotkeys } from '#src/hooks/useHotkeys.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { useLinkOrigin } from '#src/hooks/useLinkOrigin.ts'
import { toast } from '#src/utils/toast.ts'

export type OrderListGrouping = 'status' | 'performer' | undefined

const NO_PERFORMER_KEY = '__no_performer__'

interface OrdersListProps {
  patientId?: string
  data: Order[] | undefined
  isLoading: boolean
  error: Error | null
  lastViewedOrderId?: string | null
  hideCheckbox?: boolean
  display?: Partial<OrderViewDisplay>
  grouping?: OrderListGrouping
  commentByOrder?: Map<string, CommentCommunication>
  /* Search match data — when provided, rows show highlighted titles and
   * snippets; VirtualizedList switches to dynamic (auto-measured) height. */
  matchById?: Map<string, MatchInfo>
  getExtractedFields?: (id: string) => Record<string, string> | undefined
}

export const OrdersList = memo(function OrdersList({
  patientId,
  data,
  isLoading,
  error,
  lastViewedOrderId,
  hideCheckbox = false,
  display,
  grouping,
  commentByOrder,
  matchById,
  getExtractedFields,
}: OrdersListProps): JSX.Element {
  const isReadOnly = useIsReadOnly()
  const [selectedOrders, setSelectedOrders] = useState<Map<string, Order>>(
    new Map()
  )
  const [lastSelectedOrderId, setLastSelectedOrderId] = useState<string | null>(
    null
  )
  if (selectedOrders.size === 0 && lastSelectedOrderId) {
    setLastSelectedOrderId(null)
  }

  const orderAppointmentMap = useOrderAppointmentsMap(data ?? [])

  const orderListItems = useOrderListItems(data, grouping)

  const handleOrderCheckedChange = useCallback(
    (order: Order, value: boolean, isShiftClick = false) => {
      setSelectedOrders((prev) => {
        const newMap = new Map(prev)

        if (isShiftClick && lastSelectedOrderId && orderListItems) {
          // Find indices of last selected and current order in the visual list
          const lastIndex = orderListItems.findIndex(
            (item) =>
              item.type === 'order' && item.order.id === lastSelectedOrderId
          )
          const currentIndex = orderListItems.findIndex(
            (item) => item.type === 'order' && item.order.id === order.id
          )

          if (lastIndex !== -1 && currentIndex !== -1) {
            // Select all orders between last selected and current (inclusive)
            const startIndex = Math.min(lastIndex, currentIndex)
            const endIndex = Math.max(lastIndex, currentIndex)

            for (let i = startIndex; i <= endIndex; i++) {
              const item = orderListItems[i]
              // Only select actual orders, not group headers
              if (item.type === 'order') {
                newMap.set(item.order.id, item.order)
              }
            }
          }
          return newMap
        }

        // Normal single selection
        if (value) {
          newMap.set(order.id, order)
        } else {
          newMap.delete(order.id)
        }

        return newMap
      })

      // Update last selected order for shift+click functionality
      if (value) {
        setLastSelectedOrderId(order.id)
      }
    },
    [lastSelectedOrderId, orderListItems]
  )

  useHotkeys(
    'meta+a',
    (e) => {
      e.preventDefault()
      e.stopPropagation()

      if (selectedOrders.size === 0) {
        setSelectedOrders(
          new Map(data?.map((order) => [order.id, order]) ?? [])
        )
      } else {
        setSelectedOrders(new Map())
      }
    },
    { enabled: !hideCheckbox }
  )

  const isGroupHeader = useCallback(
    (index: number) => {
      const item = orderListItems[index]
      return item?.type === 'status-group' || item?.type === 'performer-group'
    },
    [orderListItems]
  )

  const rows = useMemo(
    () =>
      orderListItems.map((orderListItem) => {
        if (orderListItem.type === 'status-group') {
          const allSelected = orderListItem.orders.every((order) =>
            selectedOrders.has(order.id)
          )
          return (
            <OrderStatusGroupHeader
              key={orderListItem.key}
              status={orderListItem.status}
              orderCount={orderListItem.orders.length}
              checked={allSelected}
              hideCheckbox={hideCheckbox}
              onCheckedChange={() => {
                if (allSelected) {
                  setSelectedOrders((prev) => {
                    const newMap = new Map(prev)
                    orderListItem.orders.forEach((order) => {
                      newMap.delete(order.id)
                    })
                    return newMap
                  })
                } else {
                  setSelectedOrders((prev) => {
                    const newMap = new Map(prev)
                    orderListItem.orders.forEach((order) => {
                      newMap.set(order.id, order)
                    })
                    return newMap
                  })
                }
              }}
            />
          )
        }
        if (orderListItem.type === 'performer-group') {
          const allSelected = orderListItem.orders.every((order) =>
            selectedOrders.has(order.id)
          )
          return (
            <OrderPerformerGroupHeader
              key={orderListItem.key}
              performer={orderListItem.performer}
              orderCount={orderListItem.orders.length}
              checked={allSelected}
              hideCheckbox={hideCheckbox}
              onCheckedChange={() => {
                if (allSelected) {
                  setSelectedOrders((prev) => {
                    const newMap = new Map(prev)
                    orderListItem.orders.forEach((order) => {
                      newMap.delete(order.id)
                    })
                    return newMap
                  })
                } else {
                  setSelectedOrders((prev) => {
                    const newMap = new Map(prev)
                    orderListItem.orders.forEach((order) => {
                      newMap.set(order.id, order)
                    })
                    return newMap
                  })
                }
              }}
            />
          )
        }
        const order = orderListItem.order
        return (
          <OrderListRow
            key={orderListItem.key}
            order={order}
            hideParticipant={!!patientId}
            hideCheckbox={hideCheckbox}
            checked={selectedOrders.has(order.id)}
            onCheckedChange={(value, isShiftClick) =>
              handleOrderCheckedChange(order, value, isShiftClick)
            }
            appointment={orderAppointmentMap.get(order.reference.reference)}
            isLastViewed={order.id === lastViewedOrderId}
            display={display}
            comment={commentByOrder?.get(order.reference.reference)}
            match={order.id ? matchById?.get(order.id) : undefined}
            extractedFields={
              order.id ? getExtractedFields?.(order.id) : undefined
            }
          />
        )
      }),
    [
      orderListItems,
      patientId,
      selectedOrders,
      orderAppointmentMap,
      handleOrderCheckedChange,
      lastViewedOrderId,
      hideCheckbox,
      display,
      commentByOrder,
      matchById,
      getExtractedFields,
    ]
  )

  if (error) {
    return <div>Error: {error.message}</div>
  }

  if (isLoading) {
    return <Loader />
  }

  return (
    <div className="relative size-full">
      <VirtualizedList
        rows={rows}
        estimateSize={() => 40}
        scrollRestorationId="orders-table"
        isGroupHeader={isGroupHeader}
        dynamic={matchById !== undefined}
      />
      {!isReadOnly && selectedOrders.size > 0 && (
        <BulkActions
          selectedOrders={selectedOrders}
          setSelectedOrders={setSelectedOrders}
        />
      )}
    </div>
  )
})

function BulkActions({
  selectedOrders,
  setSelectedOrders,
}: {
  selectedOrders: Map<string, Order>
  setSelectedOrders: (orders: Map<string, Order>) => void
}): ReactNode {
  const copyIdentifiers = useCallback(() => {
    const identifiers = Array.from(selectedOrders.values())
      .map((order) => order.seenIdentifier)
      .filter(Boolean)
      .join('\n')

    if (identifiers) {
      void navigator.clipboard.writeText(identifiers)
      toast.info(`${selectedOrders.size} order identifiers copied to clipboard`)
    } else {
      toast.error('No identifiers found in selected orders')
    }
  }, [selectedOrders])

  const linkOrigin = useLinkOrigin()
  const copyUrls = useCallback(() => {
    const urls = Array.from(selectedOrders.values())
      .map((order) => `${linkOrigin()}/orders/${order.id}`)
      .join('\n')

    void navigator.clipboard.writeText(urls)
    toast.info(`${selectedOrders.size} order URLs copied to clipboard`)
  }, [linkOrigin, selectedOrders])

  const selectedOrdersArray = useMemo(
    () => Array.from(selectedOrders.values()),
    [selectedOrders]
  )

  return (
    <BulkActionBar
      count={selectedOrders.size}
      onClear={() => setSelectedOrders(new Map())}
    >
      <Button variant="outline" onClick={copyIdentifiers} className="gap-2">
        <IconCopy className="size-4" />
        Copy IDs
      </Button>
      <Button variant="outline" onClick={copyUrls} className="gap-2">
        <IconLink className="size-4" />
        Copy URLs
      </Button>
      <OrdersCommentButton orders={selectedOrdersArray} />
    </BulkActionBar>
  )
}

type OrderListItem =
  | {
      key: string
      type: 'status-group'
      status: Order['status']
      orders: Order[]
    }
  | {
      key: string
      type: 'performer-group'
      performer: string | null
      orders: Order[]
    }
  | {
      key: string
      type: 'order'
      order: Order
    }

function useOrderListItems(
  orders: Order[] | undefined,
  grouping: OrderListGrouping
): OrderListItem[] {
  return useMemo(() => {
    if (!orders) {
      return []
    }
    if (!grouping) {
      return orders.map((order) => ({
        type: 'order' as const,
        key: order.id,
        order,
      }))
    }

    if (grouping === 'performer') {
      const groupedOrders = groupBy(
        orders,
        (order) => order.performer?.reference ?? NO_PERFORMER_KEY
      )

      // Get unique performers and sort by reference string
      const performers = uniqBy(
        orders
          .map((order) => order.performer?.reference)
          .filter((ref): ref is string => !!ref),
        (ref) => ref
      ).sort()

      // Process performers first, then null performer
      return [...performers, null].flatMap((performer) => {
        const performerOrders = groupedOrders[performer ?? NO_PERFORMER_KEY]
        if (!performerOrders?.length) {
          return []
        }

        const sortedOrders = sortOrdersByTier(performerOrders)

        return [
          {
            type: 'performer-group' as const,
            key: performer ?? NO_PERFORMER_KEY,
            performer,
            orders: sortedOrders,
          },
          ...sortedOrders.map((order) => ({
            type: 'order' as const,
            key: order.id,
            order,
          })),
        ]
      })
    }

    // Status grouping
    const groupedOrders = groupBy(orders, (order) => order.status) as Partial<
      Record<Order['status'], Order[]>
    >
    return ORDERED_ORDER_STATES.flatMap((status) => {
      const statusOrders = groupedOrders[status]
      if (!statusOrders?.length) {
        return []
      }
      return [
        {
          type: 'status-group' as const,
          key: status,
          status,
          orders: statusOrders,
        },
        ...statusOrders.map((order) => ({
          type: 'order' as const,
          key: order.id,
          order,
        })),
      ]
    })
  }, [orders, grouping])
}

const ORDERED_ORDER_STATES: Order['status'][] = [
  'on-hold',
  'active',
  'coordinated',
  'effectuated',
  'draft',
  'completed',
  'revoked',
  'entered-in-error',
] as const

function OrderStatusGroupHeader({
  status,
  orderCount,
  hideCheckbox,
  checked,
  onCheckedChange,
}: {
  status: Order['status']
  orderCount: number
  hideCheckbox?: boolean
  checked?: boolean
  onCheckedChange?: (value: boolean) => void
}): ReactNode {
  return (
    <div
      className={cn(
        'group flex h-10 items-center justify-between',
        'bg-frame',
        'px-pagex-default lg:px-pagex-lg',
        !hideCheckbox && 'pr-8 pl-0 lg:pr-8 lg:pl-0'
      )}
    >
      {!hideCheckbox && (
        <TaskRowCheckbox value={checked} onChange={onCheckedChange} />
      )}
      <div className="flex flex-1 items-center gap-2">
        <IconOrderStatus status={status} />
        <TypographySubtle className="font-medium">
          {ORDER_STATUS[status]}
        </TypographySubtle>
        <TypographyDetail className="text-foreground-secondary">
          ({orderCount})
        </TypographyDetail>
      </div>
    </div>
  )
}

function OrdersCommentButton({ orders }: { orders: Order[] }): JSX.Element {
  const [open, setOpen] = useState(false)
  const { mutate, isPending } = useTransactionMutation()
  const currentPractitioner = useCurrentPractitioner()
  const handleSubmit = (values: z.infer<typeof formSchema>): void => {
    mutate(
      async (transaction) => {
        orders.forEach((order) => {
          transaction.create(
            new CommentCommunication({
              message: values.message,
              sender: currentPractitioner.reference,
              partOf: [order.reference],
            })
          )
        })
      },
      {
        onSuccess: () => {
          setOpen(false)
        },
      }
    )
  }
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogButton variant="outline" className="gap-2">
        <IconComment className="size-4 fill-current" />
        Comment…
      </DialogButton>
      <DialogDisplay>
        <OrderCommentComposerDialogContent
          isSubmitting={isPending}
          onSubmit={handleSubmit}
        />
      </DialogDisplay>
    </Dialog>
  )
}

function OrderCommentComposerDialogContent({
  isSubmitting,
  onSubmit,
}: {
  isSubmitting: boolean
  onSubmit: (values: z.infer<typeof formSchema>) => void
}): JSX.Element {
  const form = useForm({
    schema: formSchema,
    defaultValues: {
      message: '',
    },
  })
  return (
    <Form onSubmit={form.handleSubmit(onSubmit)}>
      <DialogContent className="pt-6">
        <FormField
          control={form.control}
          name="message"
          render={({ field }) => (
            <RichTextEditor
              placeholder="Leave a comment…"
              variant="unstyled"
              className="flex-1"
              contentClassName="text-sm"
              enablePractitionerMention
              {...field}
            />
          )}
        />
      </DialogContent>
      <DialogFooter>
        <Tooltip>
          <TooltipTrigger asChild>
            <SubmitButton
              size="icon"
              className="self-end rounded-full"
              submitting={isSubmitting}
            >
              <IconArrowUp className="size-4" />
            </SubmitButton>
          </TooltipTrigger>
          <TooltipContent
            side="bottom"
            className="flex items-center gap-1 px-2"
          >
            <div className="flex gap-1">
              <KBD>⌘</KBD>
              <KBD>Enter</KBD>
            </div>
            <TypographyDetail className="text-foreground-secondary">
              to submit
            </TypographyDetail>
          </TooltipContent>
        </Tooltip>
      </DialogFooter>
    </Form>
  )
}

const formSchema = z.object({
  message: z.string().min(1, { message: 'Message required' }),
})
